export type Oklch = { L: number; C: number; H: number };
export type Rgb = [number, number, number];

export const SCALE_STOPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;
export type Shade = (typeof SCALE_STOPS)[number];

const WHITE: Oklch = { L: 1, C: 0, H: 0 };
const BLACK: Oklch = { L: 0, C: 0, H: 0 };

export function oklchToRgb({ L, C, H }: Oklch): Rgb {
  const hRad = (H * Math.PI) / 180;
  const a = C * Math.cos(hRad);
  const b = C * Math.sin(hRad);

  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.291485548 * b;
  const lLin = l_ ** 3;
  const mLin = m_ ** 3;
  const sLin = s_ ** 3;

  const r = 4.0767416621 * lLin - 3.3077115913 * mLin + 0.2309699292 * sLin;
  const g = -1.2684380046 * lLin + 2.6097574011 * mLin - 0.3413193965 * sLin;
  const bl = -0.0041960863 * lLin - 0.7034186147 * mLin + 1.707614701 * sLin;

  return [r, g, bl].map((x) => Math.round(Math.max(0, Math.min(1, linearToSrgb(x))) * 255)) as Rgb;
}

function linearToSrgb(x: number): number {
  return x <= 0.0031308 ? 12.92 * x : 1.055 * x ** (1 / 2.4) - 0.055;
}

// Hue is taken from the more-saturated side so mixing with achromatic white/black
// preserves the base's hue exactly (white/black have undefined H in OKLCH).
function mixOklch(a: Oklch, b: Oklch, t: number): Oklch {
  return {
    L: a.L * (1 - t) + b.L * t,
    C: a.C * (1 - t) + b.C * t,
    H: a.C >= b.C ? a.H : b.H,
  };
}

export function generateScale(base: Oklch): Record<Shade, Rgb> {
  const stops: Record<Shade, Oklch> = {
    50: mixOklch(WHITE, base, 0.05),
    100: mixOklch(WHITE, base, 0.12),
    200: mixOklch(WHITE, base, 0.25),
    300: mixOklch(WHITE, base, 0.45),
    400: mixOklch(WHITE, base, 0.7),
    500: base,
    600: mixOklch(base, BLACK, 0.1),
    700: mixOklch(base, BLACK, 0.25),
    800: mixOklch(base, BLACK, 0.4),
    900: mixOklch(base, BLACK, 0.55),
    950: mixOklch(base, BLACK, 0.7),
  };
  return Object.fromEntries(SCALE_STOPS.map((s) => [s, oklchToRgb(stops[s])])) as Record<
    Shade,
    Rgb
  >;
}

function relLuminance([r, g, b]: Rgb): number {
  const c = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * c(r) + 0.7152 * c(g) + 0.0722 * c(b);
}

export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relLuminance(a);
  const lb = relLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}
